import { isLightTheme, resolveSemanticColor } from '@/theme/semanticTokens';
import { lightModeChartStatusColors } from '@/theme/uiTokens';
import { darken } from '@/ui/primitives/colorUtils';
import type { getThresholdChipKey } from '@/ui/composites/table/tableUtils';
import type { Theme } from '@/ui/primitives';

export const resolveTableLatestChipBackground = (theme: Theme, statusTone: string): string => {
  if (statusTone === 'green' || statusTone === 'success') {
    return darken(resolveSemanticColor(theme, 'success'), 0.22);
  }
  if (statusTone === 'red' || statusTone === 'error') {
    return darken(resolveSemanticColor(theme, 'error'), 0.22);
  }
  if (statusTone === 'orange' || statusTone === 'warning') {
    return darken(resolveSemanticColor(theme, 'warning'), 0.22);
  }
  if (statusTone === 'brown') {
    return darken(resolveSemanticColor(theme, 'brown'), 0.18);
  }
  return darken(theme.app.colors.textMuted, 0.18);
};

export const mapChipKeyToStatusTone = (
  key: ReturnType<typeof getThresholdChipKey>,
): 'neutral' | 'green' | 'red' | 'orange' | 'brown' | 'blue' | 'purple' | 'teal' => {
  if (key === 'yellow') return 'orange';
  if (key === null) return 'neutral';
  return key;
};

export const resolveTableMetricAccentColor = (
  theme: Theme,
  tone: ReturnType<typeof mapChipKeyToStatusTone>,
): string => {
  if (isLightTheme(theme)) {
    if (tone === 'green') return lightModeChartStatusColors.green;
    if (tone === 'red') return lightModeChartStatusColors.red;
    if (tone === 'orange') return lightModeChartStatusColors.orange;
    if (tone === 'blue') return lightModeChartStatusColors.blue;
    if (tone === 'purple') return lightModeChartStatusColors.purple;
    if (tone === 'teal') return lightModeChartStatusColors.teal;
    if (tone === 'brown') return lightModeChartStatusColors.brown;
    return lightModeChartStatusColors.neutral;
  }

  if (tone === 'green') return resolveSemanticColor(theme, 'success');
  if (tone === 'red') return resolveSemanticColor(theme, 'error');
  if (tone === 'orange') return resolveSemanticColor(theme, 'warning');
  if (tone === 'blue') return resolveSemanticColor(theme, 'info');
  if (tone === 'purple') return resolveSemanticColor(theme, 'accent');
  if (tone === 'teal') return resolveSemanticColor(theme, 'accentStrong');
  if (tone === 'brown') return resolveSemanticColor(theme, 'brown');
  return resolveSemanticColor(theme, 'textPrimary');
};

export const buildRangeSparkline = ({
  latestDate,
  rangeDays,
  startValue,
  endValue,
}: {
  latestDate: Date;
  rangeDays: number;
  startValue: number;
  endValue: number;
}): { sparklineData: number[]; sparklineDates: Date[] } => {
  const totalDays = Math.max(1, Math.floor(rangeDays));
  const totalPoints = totalDays + 1;
  const startDate = new Date(latestDate);
  startDate.setDate(latestDate.getDate() - totalDays);

  const sparklineData = Array.from({ length: totalPoints }, (_unused, index) => {
    const progress = totalPoints <= 1 ? 1 : index / (totalPoints - 1);
    return startValue + (endValue - startValue) * progress;
  });

  const sparklineDates = Array.from({ length: totalPoints }, (_unused, index) => {
    const pointDate = new Date(startDate);
    pointDate.setDate(startDate.getDate() + index);
    return pointDate;
  });

  return { sparklineData, sparklineDates };
};
